<template>
    <div class="wizard-liabilities">
        <transition name="dialogfade">
            <k-dialog v-if="showDialog" :title="'Liabilities'" @close="closeDialog" class="wizard-liabilities-dialog">
                <k-tabstrip :selected="selectedTab" 
                            @select="onTabSelected"
                            :tabs="liabilitiesTabs"
                            class="liabilities-tabstrip">
                    <template v-slot:summary>
                        <k-grid :data-items="vols" 
                            :data-item-key="'id'"
                            :columns="volColumns"></k-grid>
                    </template>
                    <template v-slot:documents>
                        <div class="edit-condition__attachments payoffs">
                            <div class="attachments-grid__panel-container">
                                <k-panelbar :items="panelItems" class="attachments-grid__panel">
                                    <template v-slot:attachmentsgrid>
                                        <k-grid :data-items="payoffsAttachments"
                                            :columns="attachmentColumns"
                                            :sortable="sortable"
                                            :sort="attachmentSort"
                                            class="wizard-liabilities-grid"
                                            @sortchange="attachmentSortChangeHandler"
                                            @rowclick="onOpenAttachment">
                                        </k-grid>
                                    </template>
                                </k-panelbar>
                            </div>
                                <div class="viewer__container">
                                    <WebViewer :showEditorButton="false" 
                                                :pdfContainerClass="'attachments-viewer'"
                                                :selectedAttchement="selectedAttachment" 
                                                :pdfFile="selectedAttachment.url" 
                                                v-if="!isAttachmentLoading && selectedAttachment.url" />
                                <div class="document-attachment__loader-container" v-if="isAttachmentLoading">
                                    <loader :size="'large'" :type="'pulsing'"></loader>
                                </div>
                                <div v-if="showPayoffsWarning">
                                    <p class="asset--info"><span class="k-icon k-i-information k-i-info"></span> This file currently has no Payoffs Attachments</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </k-tabstrip>
                <k-dialog-actions>
                    <k-button v-bind:class="['dialog__btn dialog__btn--save']" v-on:click="closeDialog">Close</k-button>
                </k-dialog-actions>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>
    </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import WebViewer from "../components/WebViewer.vue";
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Button } from '@progress/kendo-vue-buttons';
    import { Grid } from '@progress/kendo-vue-grid';
    import { TabStrip } from '@progress/kendo-vue-layout';
    import { PanelBar } from '@progress/kendo-vue-layout';
    import { Loader } from "@progress/kendo-vue-indicators";

    export default {
        props: ['loanId', 'showDialog', 'vols'],
        emits: ['dialogclosed', 'requesterror'],
        data() {
            return {
                attachmentColumns: [
                    { field: 'title', title: 'Title' },
                    { field: 'createdBy.entityId', title: 'Added By' },
                    { field: 'createdDate', title: 'Date Added', filter: "date", type: "date", format: "{0:MM/dd/yyyy}" },
                ],
                attachmentSort: [],
                errorMsg: '',
                errorTitle: '',
                isAttachmentLoading: false,
                liabilitiesTabs: [
                    { title: 'Documents', content: 'documents'},
                    { title: 'Summary', content: 'summary'}
                ],
                notificationMsg: '',
                panelItems: [
                    {
                        title: 'Supporting Documents',
                        content: 'attachmentsgrid',
                        expanded: false
                    }
                ],
                payoffsDocument: null,
                payoffsAttachments: [],
                selectedAttachment: { url: '', id: '', title: '' },
                selectedTab: 0,
                showAttachment: false,
                showErrorDialog: false,
                showPayoffsWarning: false,
                sortable: { allowUnsort: false, mode: 'single' },
                successNotification: false,
                volColumns: [
                    { field: 'holderName', title: 'Creditor' },
                    { field: 'liabilityType', title: 'Type' },
                    { field: 'owner', title: 'Belong To' },
                    { field: 'unpaidBalanceAmount', title: 'Balance', format: '{0:c2}' },
                    { field: 'remainingTermMonths', title: 'Months' },
                    { field: 'monthlyPaymentAmount', title: 'Payment', format: '{0:c2}' },
                    { field: 'toBePaidOff', title: 'To Be Paid Off'},
                    { field: 'toBePaidOffAmount', title: 'Paid Off Amount', format: '{0:c2}' }
                ],
            }
        },
        watch: {
            selectedTab(val) {
                if (val === 0) {
                    this.getPayoffsDocument();
                }
            },
            showDialog(val) {
                if (val) {
                    this.getPayoffsDocument();
                } else {
                    this.isAttachmentLoading = false;
                    this.payoffsDocument = null;
                    this.payoffsAttachments = [];
                    this.selectedAttachment = { url: '', id: '', title: '' };
                    this.selectedTab = 0;
                    this.showAttachment = false;
                }
            }
        },
        mounted() {
        },
        methods: {
            attachmentSortChangeHandler(evt) {
                this.attachmentSort = evt.sort;

                this.documentAttachments = orderBy(this.documentAttachments, this.attachmentSort);
            },
            base64ToBlob(base64, type='application/octet-stream') {
                const binStr = atob(base64);
                const len = binStr.length;
                const arr = new Uint8Array(len);

                for (let i = 0; i< len; i++) {
                    arr[i] = binStr.charCodeAt(i);
                }
                
                return new Blob([arr], {type: type});
            },
            closeDialog() {
                this.$emit('dialogclosed');
            },
            getPayoffsDocument() {
                this.showPayoffsWarning = false;

                let allDocuments = this.$store.state.documents;
                let allAttachments = this.$store.state.attachments;

                if (allDocuments.length > 0) {
                    this.payoffsDocument = allDocuments.find(doc => doc.title === 'Payoffs (All)');
                    
                    if (this.payoffsDocument) {
                        this.payoffsAttachments = allAttachments
                            .filter(att => att.assignedTo && this.payoffsDocument.id === att.assignedTo.entityId)
                            .sort((a, b) => b.createdDate - a.createdDate);

                            if (this.payoffsAttachments.length > 0 && !this.selectedAttachment.url) {
                                this.onOpenAttachment({ dataItem: { id: this.payoffsAttachments[0].id }})
                            } else {
                                this.showPayoffsWarning = true;
                            }
                    } else {
                        this.showPayoffsWarning = true;
                    }
                }

                this.$nextTick(() => {
                    let panel = document.querySelector('.wizard-liabilities-dialog');

                    if (panel) {
                        let panelTextEl = panel.querySelector('.k-panelbar-item-text');

                        if (panelTextEl) {
                            panelTextEl.innerHTML += `<span class="attachments-badge">${this.payoffsAttachments.length}</span>`;
                        }
                    }
                });
            },
            async onOpenAttachment(evt) {
                if (this.selectedAttachment.id === evt.dataItem.id) return;

                this.showAttachment = false;

                this.selectedAttachment.url = '';
                this.selectedAttachment.id = evt.dataItem.id;
                this.selectedAttachment.title = evt.dataItem.title;
                
                this.isAttachmentLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let attachmentsFile = {
                    loanId: this.loanId,
                    attachments: [evt.dataItem.id]
                };

                apiService.GetAttachmentsFileBytes(attachmentsFile, tokenResponse.accessToken)
                    .then(res => {
                        const blob = this.base64ToBlob(res.data.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);

                        this.isAttachmentLoading = false;
                        this.selectedAttachment.url = url;
                        this.showAttachment = true;
                    }).catch(error => {
                        this.isAttachmentLoading = false;
                        this.errorTitle = 'Failed to Open Attachment';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            onRequestError(errorTitle, errorMsg) {
                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.toggleErrorDialog();
            },
            onTabSelected(evt) {
                this.selectedTab = evt.selected;
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;
                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                    this.notificationMsg = '';
                }, 4000);
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorMsg = '';
                    this.errorTitle = '';
                }
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'k-button': Button,
            'k-grid': Grid,
            'k-tabstrip': TabStrip,
            WebViewer,
            'k-panelbar': PanelBar,
            'loader': Loader
        }
    }
</script>

<style>
    @import '/src/assets/css/editConditionDialog.css';
    @import '/src/assets/css/editDocumentDialog.css';
    @import '/src/assets/css/wizardLiabilitiesDialog.css';
</style>